import { Fragment, useState } from "react";
import { Link } from "react-router";
import { INTEGRATION_EVENT_LABEL } from "@metrickle/schema/constants";
import { isWorkspaceTopic, WORKSPACE_TOPIC_LABEL, type InboxNotification } from "@metrickle/schema";
import { useInbox, useMarkRead } from "../lib/notifications";
import { fmtAgo } from "../lib/format";
import { usePageTitle } from "../lib/page-title";
import { track } from "../lib/track";
import { Button, Card, CardList, Drawing, Empty, ErrorState, PageHeader, Segmented, notifyError, toaster } from "../components/ui";

const fmtFull = (ms: number) => new Date(ms).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/** The signed-in person's alerts from every app in every workspace they belong to. */
export function Notifications() {
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const inbox = useInbox(filter === "unread");
  const markRead = useMarkRead();
  const items = inbox.data?.pages.flatMap((p) => p.items) ?? [];
  const unread = inbox.data?.pages[0]?.unread ?? 0;

  usePageTitle("Notifications");

  const markAll = () =>
    markRead.mutate(
      { all: true },
      {
        onSuccess: () => {
          track("notifications_marked_read", { all: true });
          toaster.success({ title: "All caught up" });
        },
        onError: notifyError,
      },
    );

  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle={
          <>
            Alerts from every app you have access to, and changes to workspaces you own. Choose which ones you get in <Link to="/settings#notifications" className="text-accent-text underline underline-offset-2">Settings</Link>.
          </>
        }
        actions={
          <>
            <Segmented
              aria-label="Show"
              value={filter}
              onChange={(v) => setFilter(v as "all" | "unread")}
              items={[
                { value: "all", label: "All" },
                { value: "unread", label: unread ? `Unread (${unread > 99 ? "99+" : unread})` : "Unread" },
              ]}
            />
            <Button size="sm" disabled={!unread} pending={markRead.isPending} pendingLabel="Marking…" onClick={markAll}>
              Mark all as read
            </Button>
          </>
        }
      />
      <Card aria-busy={inbox.isPending}>
        {inbox.isError ? (
          <ErrorState title="Couldn't load your notifications" error={inbox.error} />
        ) : !inbox.isPending && !items.length ? (
          <Empty illustration={<Drawing name="allClear" />} title={filter === "unread" ? "No unread notifications" : "No notifications yet"}>
            When a task's conversions drop, a release breaks a protected task or someone reports a problem, it shows up here.
          </Empty>
        ) : (
          <CardList loading={inbox.isPending} className="border-t-0">
            {items.map((n) => (
              <Item key={n.id} n={n} onOpen={() => !n.readAt && markRead.mutate({ ids: [n.id] })} />
            ))}
          </CardList>
        )}
      </Card>
      {inbox.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button size="sm" disabled={inbox.isFetchingNextPage} onClick={() => inbox.fetchNextPage()}>
            {inbox.isFetchingNextPage ? "Loading…" : "Show older"}
          </Button>
        </div>
      )}
    </>
  );
}

function Item({ n, onOpen }: { n: InboxNotification; onOpen: () => void }) {
  const unread = !n.readAt;
  return (
    <li className="relative px-4 py-3 hover:bg-surface-2">
      <div className="flex items-start gap-3">
        {/* Unread is also said in words and shown in weight, not only by the dot. */}
        <span aria-hidden className={unread ? "mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent forced-colors:bg-[Highlight]" : "mt-1.5 h-2 w-2 shrink-0"} />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 text-xs text-ink-3">
            {/* An app's event names the app; a workspace change names the workspace. */}
            <span className="font-medium text-ink-2">{n.appName ?? n.workspaceName}</span>
            <span aria-hidden>·</span>
            <span>{(isWorkspaceTopic(n.event) ? WORKSPACE_TOPIC_LABEL[n.event] : INTEGRATION_EVENT_LABEL[n.event])?.label ?? n.event}</span>
            <span aria-hidden>·</span>
            <time dateTime={new Date(n.createdAt).toISOString()} title={fmtFull(n.createdAt)}>
              {fmtAgo(n.createdAt)}
            </time>
          </p>
          {/* drift-ok: a list row's heading, not a card header. */}
          <h2 className={unread ? "mt-0.5 text-sm font-semibold text-ink" : "mt-0.5 text-sm text-ink"}>
            {/* The link covers the row (after:inset-0), so the whole entry is one target. */}
            <Link to={n.path} onClick={onOpen} className="after:absolute after:inset-0 focus-visible:outline-offset-[-2px]">
              {unread && <span className="sr-only">Unread: </span>}
              {n.title}
            </Link>
          </h2>
          {n.text && <p className="mt-1 line-clamp-3 text-sm text-ink-2">{n.text}</p>}
          {n.fields.length > 0 && (
            <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
              {n.fields.slice(0, 4).map(([k, v]) => (
                <Fragment key={k}>
                  <dt className="text-ink-3">{k}</dt>
                  <dd className="min-w-0 truncate text-ink-2">{v}</dd>
                </Fragment>
              ))}
            </dl>
          )}
        </div>
      </div>
    </li>
  );
}
